import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Upload as UploadIcon, RefreshCw, Sparkles, Check, AlertTriangle, ScanLine, User, Phone, Pencil, Eye, History, Camera, Image as ImageIcon } from "lucide-react";

async function resizeImage(file, maxSize = 1024, quality = 0.85) {
  const draw = (src, w, h) => {
    if (w > h && w > maxSize) { h = (h * maxSize) / w; w = maxSize; }
    else if (h > maxSize) { w = (w * maxSize) / h; h = maxSize; }
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(w); canvas.height = Math.round(h);
    canvas.getContext("2d").drawImage(src, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", quality);
  };
  // Preferred: createImageBitmap honours EXIF orientation from phone cameras
  if (window.createImageBitmap) {
    try {
      const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
      const out = draw(bmp, bmp.width, bmp.height);
      bmp.close?.();
      return out;
    } catch { /* fall through (e.g. HEIC on some browsers) */ }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { try { resolve(draw(img, img.naturalWidth, img.naturalHeight)); } catch (e) { reject(e); } finally { URL.revokeObjectURL(url); } };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode")); };
    img.src = url;
  });
}

function Meter({ label, value }) {
  const color = value >= 70 ? "bg-emerald-400" : value >= 45 ? "bg-amber-400" : "bg-rose-400";
  return (
    <div>
      <div className="flex justify-between text-xs text-slate-400 mb-1">
        <span>{label}</span><span>{value}%</span>
      </div>
      <div className="h-2 rounded-full bg-purple-950/60 overflow-hidden">
        <div className={`h-full ${color} transition-all duration-700`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

function ProfileForm({ t, user, onSaved }) {
  const [name, setName] = useState(user?.display_name || user?.name || "");
  const [mobile, setMobile] = useState(user?.mobile || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/profile", { display_name: name, mobile });
      onSaved(data);
      toast.success(t.upload.profileSaved);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="glass-strong rounded-3xl p-6 sm:p-8" data-testid="profile-form">
      <h2 className="font-serif text-xl text-amber-100 mb-1">{t.upload.profileTitle}</h2>
      <p className="text-sm text-slate-400 mb-6">{t.upload.profileSub}</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs text-slate-400 mb-1 block">{t.upload.yourName}</span>
          <div className="flex items-center gap-2 rounded-xl bg-white/5 border border-white/10 px-3 focus-within:border-amber-400/50">
            <User size={16} className="text-amber-300/70 shrink-0" />
            <input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t.upload.namePh}
              className="w-full bg-transparent py-3 text-slate-100 outline-none" />
          </div>
        </label>
        <label className="block">
          <span className="text-xs text-slate-400 mb-1 block">{t.upload.yourMobile}</span>
          <div className="flex items-center gap-2 rounded-xl bg-white/5 border border-white/10 px-3 focus-within:border-amber-400/50">
            <Phone size={16} className="text-amber-300/70 shrink-0" />
            <span className="text-slate-400 text-sm">+91</span>
            <input data-testid="profile-mobile-input" value={mobile} inputMode="numeric" maxLength={10}
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="98765 43210"
              className="w-full bg-transparent py-3 text-slate-100 outline-none tracking-wider" />
          </div>
        </label>
      </div>
      <button onClick={save} disabled={busy || name.trim().length < 2 || mobile.length !== 10} data-testid="profile-save-btn"
        className="btn-gold rounded-full px-8 py-3 mt-6 flex items-center gap-2 gold-glow disabled:opacity-50 w-full sm:w-auto justify-center">
        <Check size={18} /> {t.upload.continueBtn}
      </button>
      <p className="text-[11px] text-slate-500 mt-3">{t.upload.privacyNote}</p>
    </div>
  );
}

function ExistingReading({ t, lang, reading, onFresh }) {
  const navigate = useNavigate();
  const date = new Date(reading.created_at).toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { day: "2-digit", month: "long", year: "numeric" });
  return (
    <div className="glass-strong rounded-3xl p-6 sm:p-8 text-center" data-testid="existing-reading-card">
      <div className="mx-auto h-14 w-14 rounded-full grid place-items-center border border-amber-500/40 bg-purple-900/50 mb-4">
        <History className="text-amber-300" size={24} />
      </div>
      <h2 className="font-serif text-xl sm:text-2xl text-amber-100">{t.upload.existingTitle}</h2>
      <p className="text-sm text-slate-400 mt-2 max-w-md mx-auto">{t.upload.existingSub} · {date}</p>
      <div className="action-row mt-6">
        <button onClick={() => navigate(`/result/${reading.reading_id}`)} data-testid="view-existing-btn" className="btn-gold rounded-full px-8 py-3 flex items-center gap-2 gold-glow">
          <Eye size={18} /> {t.upload.viewExisting}
        </button>
        <button onClick={onFresh} data-testid="fresh-reading-btn" className="btn-ghost-gold rounded-full px-6 py-3 flex items-center gap-2">
          <RefreshCw size={16} /> {t.upload.freshReading}
        </button>
      </div>
    </div>
  );
}

export default function Upload() {
  const { t, lang, pick, user, setUser, authLoading } = useApp();
  const navigate = useNavigate();
  const inputRef = useRef();
  const cameraRef = useRef();
  const [image, setImage] = useState(null);
  const [editProfile, setEditProfile] = useState(false);
  const [existing, setExisting] = useState(undefined);
  const [fresh, setFresh] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setExisting(null); return; }
    api.get("/palm/readings").then(({ data }) => {
      setExisting(data.find((r) => r.status === "ready" || r.status === "processing") || null);
    }).catch(() => setExisting(null));
  }, [authLoading, user?.user_id]); // eslint-disable-line
  const [checking, setChecking] = useState(false);
  const [reading, setReading] = useState(false);
  const [quality, setQuality] = useState(null);

  const [loadingPhoto, setLoadingPhoto] = useState(false);
  const onFile = async (e) => {
    const input = e.target;
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;
    setQuality(null);
    setLoadingPhoto(true);
    try {
      const dataUrl = await resizeImage(file);
      setImage(dataUrl);
      runQuality(dataUrl);
    } catch {
      toast.error(t.upload.photoReadError);
    } finally {
      setLoadingPhoto(false);
    }
  };

  const runQuality = async (dataUrl) => {
    setChecking(true);
    setQuality(null);
    try {
      const { data } = await api.post("/palm/quality-check", { image: dataUrl });
      setQuality(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Quality check failed");
    } finally {
      setChecking(false);
    }
  };

  const doRead = async () => {
    setReading(true);
    try {
      const { data } = await api.post("/palm/read", { image });
      navigate(`/result/${data.reading_id}`, { state: { reading: data } });
    } catch (err) {
      if (err?.response?.status === 401) { navigate("/login"); return; }
      toast.error(err?.response?.data?.detail || "Reading failed");
      setReading(false);
    }
  };

  const scores = quality?.scores || {};
  const good = quality?.clarity_ok;
  const notPalm = quality && quality.is_palm === false;

  if (reading) {
    return (
      <div className="min-h-[70vh] grid place-items-center px-6">
        <div className="text-center">
          <div className="relative mx-auto h-28 w-28 mb-8">
            <div className="absolute inset-0 rounded-full border-2 border-amber-400/30 spin-slow" />
            <div className="absolute inset-3 rounded-full border border-purple-400/40 spin-slow" style={{ animationDirection: "reverse" }} />
            <Sparkles className="absolute inset-0 m-auto text-amber-300" size={36} />
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl text-amber-100">{t.upload.reading}</h2>
          <p className="mt-3 text-slate-400 max-w-md mx-auto">{t.upload.readingSub}</p>
        </div>
      </div>
    );
  }

  const profileDone = !!(user?.display_name && user?.mobile);
  const needProfile = user && (!profileDone || editProfile);

  return (
    <div className="mx-auto max-w-3xl px-6 py-14">
      <div className="text-center mb-10 rise-in">
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-amber-50">{t.upload.title}</h1>
        <p className="mt-3 text-slate-300">{t.upload.subtitle}</p>
      </div>

      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="palm-file-input" />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="palm-camera-input" />

      {needProfile ? (
        <ProfileForm t={t} user={user} onSaved={(d) => { setUser({ ...user, display_name: d.display_name, mobile: d.mobile }); setEditProfile(false); }} />
      ) : existing && !fresh && !image ? (
        <ExistingReading t={t} lang={lang} reading={existing} onFresh={() => setFresh(true)} />
      ) : (
      <>
      {profileDone && (
        <div className="flex items-center justify-center gap-2 text-sm text-slate-300 mb-5" data-testid="profile-summary">
          <User size={14} className="text-amber-300" /> <span className="text-amber-100">{user.display_name}</span>
          <span className="text-slate-500">·</span> <Phone size={14} className="text-amber-300" /> <span>+91 {user.mobile}</span>
          <button onClick={() => setEditProfile(true)} data-testid="profile-edit-btn" className="ml-1 text-amber-300/80 hover:text-amber-200 inline-flex items-center gap-1"><Pencil size={12} /> {t.upload.edit}</button>
        </div>
      )}
      {!image ? (
        <div className="glass rounded-3xl border-2 border-dashed border-amber-500/30 p-6 sm:p-10 relative" data-testid="photo-source-card">
          {loadingPhoto && (
            <div className="absolute inset-0 rounded-3xl bg-black/60 grid place-items-center z-10" data-testid="photo-loading">
              <span className="flex items-center gap-2 text-amber-200"><ScanLine className="animate-pulse" /> {t.upload.loadingPhoto}</span>
            </div>
          )}
          <div className="flex flex-col items-center gap-3 mb-6 text-center">
            <div className="h-16 w-16 rounded-2xl grid place-items-center bg-gradient-to-br from-amber-500/20 to-purple-600/20 border border-amber-500/30">
              <UploadIcon className="text-amber-300" size={28} />
            </div>
            <span className="font-serif text-lg text-amber-100">{t.upload.choose}</span>
            <span className="text-xs text-slate-400 max-w-sm">{t.upload.sourceHint}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button onClick={() => cameraRef.current?.click()} data-testid="take-photo-btn"
              className="btn-gold rounded-2xl py-5 flex flex-col items-center justify-center gap-2 gold-glow">
              <Camera size={26} />
              <span className="font-medium">{t.upload.takePhoto}</span>
              <span className="text-[11px] opacity-80 font-normal">{t.upload.takePhotoSub}</span>
            </button>
            <button onClick={() => inputRef.current?.click()} data-testid="choose-photo-btn"
              className="btn-ghost-gold rounded-2xl py-5 flex flex-col items-center justify-center gap-2">
              <ImageIcon size={26} />
              <span className="font-medium">{t.upload.fromGallery}</span>
              <span className="text-[11px] opacity-80 font-normal">{t.upload.fromGallerySub}</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="relative rounded-3xl overflow-hidden border border-amber-500/25 gold-glow">
            <img src={image} alt="palm" className="w-full max-h-[55vh] object-contain bg-black/40" data-testid="palm-preview" />
            {checking && <div className="scan-line" />}
            {checking && (
              <div className="absolute inset-0 bg-black/40 grid place-items-center">
                <span className="flex items-center gap-2 text-amber-200"><ScanLine className="animate-pulse" /> {t.upload.checking}</span>
              </div>
            )}
          </div>

          {quality && (
            <div className="glass rounded-2xl p-6" data-testid="quality-result">
              <div className={`flex items-center gap-2 mb-4 font-serif text-lg ${good ? "text-emerald-300" : "text-amber-300"}`}>
                {good ? <Check size={20} /> : <AlertTriangle size={20} />}
                {notPalm ? t.upload.notPalm : good ? t.upload.qualityGood : t.upload.qualityBad}
              </div>
              {!notPalm && (
                <div className="grid gap-3 sm:grid-cols-3">
                  <Meter label={t.upload.brightness} value={scores.brightness ?? 0} />
                  <Meter label={t.upload.sharpness} value={scores.sharpness ?? 0} />
                  <Meter label={t.upload.alignment} value={scores.alignment ?? 0} />
                </div>
              )}
              <p className="mt-4 text-sm text-slate-300">{pick({ en: quality.feedback_en, hi: quality.feedback_hi })}</p>
            </div>
          )}

          <div className="action-row">
            <button onClick={() => cameraRef.current?.click()} data-testid="retake-btn" className="btn-ghost-gold rounded-full px-6 py-3 flex items-center gap-2">
              <Camera size={16} /> {t.upload.retake}
            </button>
            <button onClick={() => inputRef.current?.click()} data-testid="change-photo-btn" className="btn-ghost-gold rounded-full px-6 py-3 flex items-center gap-2">
              <ImageIcon size={16} /> {t.upload.change}
            </button>
            {quality && !checking && (
              good ? (
                <button onClick={doRead} data-testid="read-palm-btn" className="btn-gold rounded-full px-8 py-3 flex items-center gap-2 gold-glow">
                  <Sparkles size={18} /> {t.upload.readNow}
                </button>
              ) : !notPalm ? (
                <button onClick={doRead} data-testid="read-anyway-btn" className="btn-gold rounded-full px-8 py-3 flex items-center gap-2 opacity-90">
                  <Sparkles size={18} /> {t.upload.useAnyway}
                </button>
              ) : null
            )}
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
